/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  X, Loader2, Radio, Clock, ScrollText, MousePointerClick,
  ExternalLink, Eye, RotateCcw, Smartphone, FileSearch, FolderOpen, Layers,
} from "lucide-react";
import { adminAudienceVisitorDossier } from "../../lib/api";
import { NetworkBadge, ConfidenceBadge } from "./NetworkBadges";

/**
 * VisitorDossierDrawer — admin-only side-drawer that opens on top of the
 * Live audience panel. Presents an anonymous visitor's behaviour as an
 * Archive case file (aged paper / interrogation lamp accents) rather than
 * a generic analytics page.
 *
 * Privacy: this drawer never displays IPs, exact UA strings, or any PII
 * the backend doesn't already expose. Subscriber email is only present if
 * the user willingly subscribed.
 */

const fmtClock = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return `${d.toLocaleDateString()} · ${d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}`;
  } catch { return iso; }
};
const fmtAgo = (iso) => {
  if (!iso) return "—";
  try {
    const t = new Date(iso).getTime();
    const sec = Math.max(0, Math.floor((Date.now() - t) / 1000));
    if (sec < 5) return "just now";
    if (sec < 60) return `${sec}s ago`;
    if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
    if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
    return `${Math.floor(sec / 86400)}d ago`;
  } catch { return "—"; }
};
const fmtSeconds = (n) => {
  const s = Math.max(0, Math.round(Number(n) || 0));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  if (m < 60) return `${m}m ${r}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
};

const KIND_DOT = {
  page_view: "#A39E93",
  dossier_open: "#D92D20",
  source_click: "#5DD4B3",
  node_click: "#CBB892",
  scroll_depth: "#9FA8D4",
  read_time: "#CBB892",
  return_visit: "#E0A526",
};

const KindBadge = ({ kind }) => (
  <span
    className="inline-flex items-center px-1.5 py-0.5 border font-mono text-[8.5px] uppercase tracking-[0.25em]"
    style={{ color: KIND_DOT[kind] || "#A39E93", borderColor: `${KIND_DOT[kind] || "#454038"}55` }}
  >
    {(kind || "").replace(/_/g, " ")}
  </span>
);

const Stamp = ({ children }) => (
  <span className="inline-block font-mono text-[8.5px] uppercase tracking-[0.3em] text-[#D92D20]/70 border border-[#D92D20]/40 px-1.5 py-0.5 -rotate-2">
    {children}
  </span>
);

const Stat = ({ label, value, testid, sub = null }) => (
  <div
    data-testid={testid}
    className="border border-[#2D2A26] bg-[#0d0c0a] px-3 py-2.5"
  >
    <div className="font-mono text-[8.5px] uppercase tracking-[0.3em] text-[#757065]">{label}</div>
    <div className="font-display text-xl text-[#E4DFD3] mt-0.5">{value ?? "—"}</div>
    {sub != null && (
      <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#757065] mt-0.5">{sub}</div>
    )}
  </div>
);

const SectionHeader = ({ icon: Icon, children, count }) => (
  <div className="flex items-center justify-between mb-2">
    <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20]">
      {Icon ? <Icon size={12} /> : null} // {children}
    </div>
    {count !== undefined && (
      <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#757065]">{count}</div>
    )}
  </div>
);

const ScrollBar = ({ pct }) => {
  const p = Math.max(0, Math.min(100, Number(pct) || 0));
  return (
    <div className="relative h-1.5 bg-[#1a1a1a] border border-[#2D2A26]" title={`${Math.round(p)}%`}>
      <div className="absolute inset-y-0 left-0 bg-[#D92D20]/70" style={{ width: `${p}%` }} />
    </div>
  );
};

const RouteRow = ({ row, idx }) => (
  <div
    className="border border-[#2D2A26] bg-[#0F0F0F] p-3 mb-2"
    data-testid={`dossier-route-row-${idx}`}
  >
    <div className="flex items-baseline justify-between gap-2 mb-1">
      <div className="font-mono text-xs text-[#E4DFD3] truncate" title={row.route}>{row.route}</div>
      <div className="font-mono text-[10px] text-[#CBB892] tabular-nums whitespace-nowrap">
        {fmtSeconds(row.read_seconds)} · {Math.round(row.max_scroll_pct || 0)}%
      </div>
    </div>
    <ScrollBar pct={row.max_scroll_pct} />
    <div className="flex flex-wrap items-center gap-3 mt-2 font-mono text-[9.5px] uppercase tracking-[0.22em] text-[#757065]">
      <span className="flex items-center gap-1"><Eye size={10} /> {row.page_views} view{row.page_views === 1 ? "" : "s"}</span>
      <span className="flex items-center gap-1"><MousePointerClick size={10} /> {row.node_clicks} node</span>
      <span className="flex items-center gap-1"><ExternalLink size={10} /> {row.source_clicks} src</span>
      {row.dossier_slug ? (
        <a
          href={`/topic/${row.dossier_slug.replace(/^epstein:/, "")}`}
          target="_blank" rel="noopener noreferrer"
          className="text-[#A39E93] hover:text-[#D92D20] inline-flex items-center gap-1"
        >
          dossier <ExternalLink size={9} />
        </a>
      ) : null}
    </div>
  </div>
);

const TimelineRow = ({ ev, idx }) => {
  const dot = KIND_DOT[ev.kind] || "#454038";
  return (
    <div
      className="grid grid-cols-12 gap-2 items-baseline px-2 py-1.5 border-b border-[#1a1816] last:border-b-0"
      data-testid={`dossier-timeline-row-${idx}`}
    >
      <div className="col-span-3 font-mono text-[9px] text-[#757065] whitespace-nowrap">
        {fmtClock(ev.ts)}
      </div>
      <div className="col-span-3"><KindBadge kind={ev.kind} /></div>
      <div className="col-span-5 font-mono text-[11px] text-[#E4DFD3] truncate" title={ev.route}>
        {ev.route}
        {(ev.label || ev.meta) ? (
          <span className="ml-2 text-[#A39E93]">· {ev.label || ev.meta}</span>
        ) : null}
        {ev.value != null && (ev.kind === "scroll_depth" || ev.kind === "read_time") ? (
          <span className="ml-2 text-[#CBB892]">
            {ev.kind === "scroll_depth" ? `${Math.round(ev.value)}%` : fmtSeconds(ev.value)}
          </span>
        ) : null}
      </div>
      <div className="col-span-1 text-right">
        <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ backgroundColor: dot }} />
      </div>
    </div>
  );
};

export const VisitorDossierDrawer = ({ open, sessionId, token, userToken, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!sessionId) return;
    setLoading(true); setError(null);
    try {
      const res = await adminAudienceVisitorDossier(token, sessionId, userToken);
      setData(res);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load dossier.");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [token, userToken, sessionId]);

  useEffect(() => {
    if (open && sessionId) load();
  }, [open, sessionId, load]);

  // Close on ESC
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const sess = data?.session || {};
  const isLive = !!sess.is_live;
  const sourcesOpened = data?.sources_opened || [];
  const nodesClicked = data?.nodes_clicked || [];
  const perRoute = data?.per_route || [];
  const dossiers = data?.dossiers_touched || [];
  const timeline = data?.timeline || [];

  const headerStats = useMemo(() => [
    { label: "Read time", value: fmtSeconds(sess.total_read_seconds || 0), testid: "dossier-stat-read-time" },
    { label: "Max scroll", value: `${Math.round(sess.max_scroll_pct || 0)}%`, testid: "dossier-stat-scroll" },
    { label: "Events",     value: sess.event_count ?? 0, testid: "dossier-stat-events" },
    { label: "Return",     value: sess.return_visits ?? 0, sub: "≥30m gaps", testid: "dossier-stat-returns" },
  ], [sess]);

  if (!open) return null;
  return (
    <>
      {/* Scrim */}
      <button
        type="button"
        aria-label="Close visitor dossier"
        onClick={onClose}
        data-testid="dossier-scrim"
        className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm cursor-default"
      />
      {/* Drawer */}
      <aside
        role="dialog"
        aria-modal="true"
        data-testid="visitor-dossier-drawer"
        className="fixed z-50 right-0 top-0 h-full w-full sm:w-[640px] max-w-full bg-[#0a0907] border-l border-[#3A352E] shadow-[0_0_60px_rgba(0,0,0,0.8)] overflow-y-auto"
      >
        {/* Interrogation-lamp aesthetic top edge */}
        <div
          className="absolute inset-x-0 top-0 h-24 pointer-events-none"
          style={{ background: "radial-gradient(ellipse at top, rgba(217,45,32,0.18) 0%, rgba(0,0,0,0) 70%)" }}
        />

        {/* Header */}
        <header className="relative z-10 px-5 pt-5 pb-4 border-b border-[#2D2A26] bg-[#0a0907]/95 backdrop-blur-sm">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <Stamp>CONFIDENTIAL · INTERNAL ONLY</Stamp>
                {isLive ? (
                  <span className="inline-flex items-center gap-1 font-mono text-[9px] uppercase tracking-[0.3em] text-[#5DD4B3]">
                    <Radio size={10} className="animate-pulse" /> LIVE NOW
                  </span>
                ) : (
                  <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#757065]">offline</span>
                )}
              </div>
              <h2 className="font-display text-2xl text-[#E4DFD3] leading-tight">
                Visitor Dossier
              </h2>
              <div className="mt-1 flex items-baseline gap-3 flex-wrap">
                <div className="font-mono text-[15px] tracking-[0.22em] text-[#CBB892]" data-testid="dossier-callsign">
                  {sess.callsign || "GHOST-0000"}
                </div>
                <div className="font-mono text-[9.5px] uppercase tracking-[0.3em] text-[#757065]">
                  CASE FILE · {(sess.session_id || sessionId || "").slice(0, 8)}
                </div>
              </div>
              {(sess.achievements && sess.achievements.length > 0) ? (
                <div className="mt-2 flex flex-wrap gap-1.5" data-testid="dossier-achievements">
                  {sess.achievements.map((a) => (
                    <span
                      key={a}
                      className="inline-block font-mono text-[8.5px] tracking-[0.3em] uppercase px-1.5 py-0.5 border border-[#D92D20]/40 text-[#D92D20]"
                      data-testid={`dossier-achievement-${a.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {a}
                    </span>
                  ))}
                </div>
              ) : null}
              <div className="font-mono text-[9.5px] uppercase tracking-[0.25em] text-[#757065] mt-2">
                {sess.subscriber_email ? (
                  <span className="text-[#CBB892]">subscriber · {sess.subscriber_email}</span>
                ) : (
                  <span>anonymous asset · persistent callsign · no identity on file</span>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              data-testid="dossier-close-btn"
              className="border border-[#454038] hover:border-[#D92D20] text-[#A39E93] p-2"
              aria-label="Close"
            >
              <X size={14} />
            </button>
          </div>

          {/* Meta strip */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-4">
            {headerStats.map((s) => (
              <Stat key={s.label} {...s} />
            ))}
          </div>
          <div className="flex flex-wrap gap-4 mt-3 font-mono text-[9.5px] uppercase tracking-[0.25em] text-[#757065]">
            <span className="inline-flex items-center gap-1"><Clock size={10} /> first seen {fmtAgo(sess.first_seen)}</span>
            <span className="inline-flex items-center gap-1"><Eye size={10} /> last seen {fmtAgo(sess.last_seen)}</span>
            <span className="inline-flex items-center gap-1"><Smartphone size={10} /> {sess.device || "—"}</span>
            <span className="inline-flex items-center gap-1"><RotateCcw size={10} /> {sess.heartbeats || 0} pings</span>
          </div>
        </header>

        {/* Body */}
        <div className="relative z-10 p-5 space-y-6">
          {loading && !data ? (
            <div className="flex items-center justify-center py-10 text-[#A39E93]">
              <Loader2 size={16} className="animate-spin mr-2" />
              <span className="font-mono text-[10px] uppercase tracking-[0.3em]">Loading dossier…</span>
            </div>
          ) : error ? (
            <div className="border border-[#D92D20]/40 bg-[#1a0808] text-[#D92D20] px-4 py-3 font-mono text-[10px] uppercase tracking-[0.25em]" data-testid="dossier-error">
              // {error}
            </div>
          ) : (
            <>
              {/* Origin (privacy-safe, coarse only) */}
              <section data-testid="dossier-section-origin">
                <SectionHeader icon={ScrollText}>ORIGIN</SectionHeader>
                <div className="grid grid-cols-2 gap-2">
                  <Stat testid="dossier-origin-country" label="Country"  value={sess.country  ? (typeof Intl !== "undefined" && Intl.DisplayNames ? (() => { try { return new Intl.DisplayNames(["en"], { type: "region" }).of(sess.country) || sess.country; } catch { return sess.country; } })() : sess.country) : "Unknown"} />
                  <Stat testid="dossier-origin-region"  label="Region"   value={sess.region   || "—"} />
                  <Stat testid="dossier-origin-city"    label="City"     value={sess.city     || "—"} />
                  <Stat testid="dossier-origin-timezone" label="Timezone" value={sess.timezone || "—"} />
                </div>

                {/* Intelligence: network classification + location confidence */}
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <NetworkBadge type={sess.network_type} testid="dossier-origin-network" />
                  <ConfidenceBadge level={sess.location_confidence} testid="dossier-origin-loc-confidence" />
                </div>

                <div className="mt-2 font-mono text-[9px] uppercase tracking-[0.3em] text-[#757065]" data-testid="dossier-origin-confidence">
                  // Geo source:{" "}
                  <span className={
                    sess.geo_confidence === "header" ? "text-[#5DD4B3]" :
                    sess.geo_confidence === "geo"    ? "text-[#CBB892]" :
                                                      "text-[#E0A526]"
                  }>
                    {sess.geo_confidence === "header" ? "proxy header" :
                     sess.geo_confidence === "geo"    ? "ip geolocation (coarse)" :
                                                       "unavailable"}
                  </span>
                  <span className="ml-2 text-[#454038]">· no raw IP retained</span>
                </div>
              </section>

              {/* Reading path / per-route engagement */}
              <section data-testid="dossier-section-routes">
                <SectionHeader icon={ScrollText} count={`${perRoute.length} route${perRoute.length === 1 ? "" : "s"}`}>
                  READING PATH
                </SectionHeader>
                {perRoute.length === 0 ? (
                  <div className="text-[#757065] font-mono text-[10px] uppercase tracking-[0.3em] italic">No engagement recorded.</div>
                ) : (
                  perRoute.map((r, i) => <RouteRow key={r.route || i} row={r} idx={i} />)
                )}
              </section>

              {/* Dossiers touched */}
              {dossiers.length > 0 && (
                <section data-testid="dossier-section-touched">
                  <SectionHeader icon={Layers} count={dossiers.length}>
                    DOSSIERS TOUCHED
                  </SectionHeader>
                  <div className="flex flex-wrap gap-2">
                    {dossiers.map((d) => (
                      <a
                        key={d.slug}
                        href={`/topic/${d.slug.replace(/^epstein:/, "")}`}
                        target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 border border-[#454038] hover:border-[#D92D20] text-[#A39E93] hover:text-[#E4DFD3] px-2 py-1 font-mono text-[10px] uppercase tracking-[0.25em]"
                      >
                        {d.title}<ExternalLink size={9} />
                      </a>
                    ))}
                  </div>
                </section>
              )}

              {/* Sources opened */}
              <section data-testid="dossier-section-sources">
                <SectionHeader icon={FileSearch} count={sourcesOpened.length}>
                  SOURCE LINKS OPENED
                </SectionHeader>
                {sourcesOpened.length === 0 ? (
                  <div className="text-[#757065] font-mono text-[10px] uppercase tracking-[0.3em] italic">No outbound source clicks.</div>
                ) : (
                  <div className="border border-[#2D2A26] bg-[#0F0F0F]">
                    {sourcesOpened.slice().reverse().slice(0, 30).map((s, i) => (
                      <div
                        key={`${s.ts}-${i}`}
                        className="px-3 py-2 grid grid-cols-12 gap-2 items-baseline border-b border-[#1a1816] last:border-b-0"
                        data-testid={`dossier-source-row-${i}`}
                      >
                        <div className="col-span-3 font-mono text-[9px] text-[#757065] whitespace-nowrap">
                          {fmtAgo(s.ts)}
                        </div>
                        <div className="col-span-7 font-mono text-[11px] text-[#E4DFD3] truncate" title={s.label}>
                          {s.label || "(unknown source)"}
                        </div>
                        <div className="col-span-2 text-right font-mono text-[9px] uppercase tracking-[0.25em] text-[#5DD4B3] truncate">
                          {s.meta || "external"}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </section>

              {/* Nodes clicked on the board */}
              <section data-testid="dossier-section-nodes">
                <SectionHeader icon={FolderOpen} count={nodesClicked.length}>
                  EVIDENCE NODES CLICKED
                </SectionHeader>
                {nodesClicked.length === 0 ? (
                  <div className="text-[#757065] font-mono text-[10px] uppercase tracking-[0.3em] italic">No board nodes clicked.</div>
                ) : (
                  <div className="border border-[#2D2A26] bg-[#0F0F0F]">
                    {nodesClicked.slice().reverse().slice(0, 30).map((n, i) => (
                      <div
                        key={`${n.ts}-${i}`}
                        className="px-3 py-2 grid grid-cols-12 gap-2 items-baseline border-b border-[#1a1816] last:border-b-0"
                        data-testid={`dossier-node-row-${i}`}
                      >
                        <div className="col-span-3 font-mono text-[9px] text-[#757065] whitespace-nowrap">
                          {fmtAgo(n.ts)}
                        </div>
                        <div className="col-span-7 font-mono text-[11px] text-[#E4DFD3] truncate" title={n.label}>
                          {n.label || "(unknown node)"}
                        </div>
                        <div className="col-span-2 text-right font-mono text-[9px] uppercase tracking-[0.25em] text-[#CBB892] truncate">
                          {n.meta || "node"}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </section>

              {/* Full timeline */}
              <section data-testid="dossier-section-timeline">
                <SectionHeader count={`${timeline.length} event${timeline.length === 1 ? "" : "s"}`}>
                  CHRONOLOGY
                </SectionHeader>
                <div className="border border-[#2D2A26] bg-[#0F0F0F]">
                  {timeline.length === 0 ? (
                    <div className="px-3 py-4 text-[#757065] font-mono text-[10px] uppercase tracking-[0.3em] italic">No events captured in window.</div>
                  ) : (
                    timeline.slice().reverse().slice(0, 200).map((ev, i) => (
                      <TimelineRow key={`${ev.ts}-${i}`} ev={ev} idx={i} />
                    ))
                  )}
                </div>
              </section>

              <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#757065] pt-2 border-t border-[#2D2A26]">
                // tracked anonymously · no IP · no keystrokes · no off-site signals · 35-day retention
              </div>
            </>
          )}
        </div>
      </aside>
    </>
  );
};

export default VisitorDossierDrawer;
